import React, { useState } from 'react';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ITMOMetadataPanel from './components/ITMOMetadataPanel';
import AuthorizationTable from './components/AuthorizationTable';
import DoubleCountingPreventionPanel from './components/DoubleCountingPreventionPanel';
import ComplianceReportGenerator from './components/ComplianceReportGenerator';

const Article6Gateway = () => {
  const [selectedCredits, setSelectedCredits] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [notifications, setNotifications] = useState([
    {
      id: 'notif-001',
      type: 'authorization_update',
      message: 'Brazil has updated authorization status for 3 credit batches',
      timestamp: '2025-01-21T09:15:00Z',
      severity: 'info',
      read: false
    },
    {
      id: 'notif-002',
      type: 'conflict_detected',
      message: 'Double-counting conflict detected for VCS-2847-2023-001',
      timestamp: '2025-01-21T08:30:00Z',
      severity: 'warning',
      read: false
    },
    {
      id: 'notif-003',
      type: 'ca_required',
      message: 'Corresponding adjustment required for 2 credit transfers',
      timestamp: '2025-01-21T07:45:00Z',
      severity: 'error',
      read: true
    }
  ]);

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
    { id: 'authorizations', label: 'Authorizations', icon: 'FileCheck' },
    { id: 'conflicts', label: 'Conflicts', icon: 'Shield' },
    { id: 'reports', label: 'Reports', icon: 'FileText' }
  ];

  const handleSelectionChange = (creditIds) => {
    setSelectedCredits(creditIds);
  };

  const handleBulkAction = (action, creditIds) => {
    console.log(`Performing ${action} on credits:`, creditIds);
    // Simulate API call
    setTimeout(() => {
      setNotifications(prev => [...prev, {
        id: `notif-${Date.now()}`,
        type: 'bulk_action',
        message: `${action?.replace('_', ' ')} completed for ${creditIds?.length} credits`,
        timestamp: new Date()?.toISOString(),
        severity: 'success',
        read: false
      }]);
    }, 1000);
  };

  const handleCreditAction = (action, creditId) => {
    console.log(`Performing ${action} on credit:`, creditId);
    // Simulate API call
    setTimeout(() => {
      setNotifications(prev => [...prev, {
        id: `notif-${Date.now()}`,
        type: 'credit_action',
        message: `${action?.replace('_', ' ')} completed for credit ${creditId}`,
        timestamp: new Date()?.toISOString(),
        severity: 'success',
        read: false
      }]);
    }, 1000);
  };

  const handleResolveConflict = (conflictId, action) => {
    console.log(`Resolving conflict ${conflictId} with action:`, action);
    // Simulate API call
    setTimeout(() => {
      setNotifications(prev => [...prev, {
        id: `notif-${Date.now()}`,
        type: 'conflict_resolution',
        message: `Conflict ${conflictId} resolved using ${action?.replace('_', ' ')}`,
        timestamp: new Date()?.toISOString(),
        severity: 'success',
        read: false
      }]);
    }, 1000);
  };

  const handleGenerateReport = (config) => {
    console.log('Generating report with config:', config);
    // Simulate report generation
    setTimeout(() => {
      setNotifications(prev => [...prev, {
        id: `notif-${Date.now()}`,
        type: 'report_generated',
        message: `${config?.reportType} report generated successfully`,
        timestamp: new Date()?.toISOString(),
        severity: 'success',
        read: false
      }]);
    }, 2000);
  };

  const getNotificationIcon = (type) => {
    const icons = {
      authorization_update: 'FileCheck',
      conflict_detected: 'AlertTriangle',
      ca_required: 'RefreshCw',
      bulk_action: 'CheckSquare',
      credit_action: 'FileText',
      conflict_resolution: 'Shield',
      report_generated: 'Download'
    };
    return icons?.[type] || 'Bell';
  };

  const getNotificationColor = (severity) => {
    const colors = {
      success: 'text-success bg-success/10',
      info: 'text-primary bg-primary/10',
      warning: 'text-warning bg-warning/10',
      error: 'text-error bg-error/10'
    };
    return colors?.[severity] || colors?.info;
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const unreadNotifications = notifications?.filter(n => !n?.read)?.length;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-card border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name="Globe" size={24} className="text-primary" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-card-foreground">Article 6 Gateway</h1>
                <p className="text-muted-foreground">International carbon market compliance and authorization management</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              {/* Notifications */}
              <div className="relative">
                <Button variant="outline" size="sm" iconName="Bell">
                  Notifications
                  {unreadNotifications > 0 && (
                    <span className="absolute -top-2 -right-2 w-5 h-5 bg-error text-error-foreground text-xs rounded-full flex items-center justify-center">
                      {unreadNotifications}
                    </span>
                  )}
                </Button>
              </div>
              <Button variant="outline" size="sm" iconName="Settings">
                Settings
              </Button>
              <Button variant="default" size="sm" iconName="RefreshCw">
                Sync Registries
              </Button>
            </div>
          </div>
        </div>
      </div>
      {/* Navigation Tabs */}
      <div className="bg-card border-b border-border">
        <div className="max-w-7xl mx-auto px-6">
          <nav className="flex space-x-8">
            {tabs?.map((tab) => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center space-x-2 py-4 px-2 border-b-2 text-sm font-medium transition-colors ${
                  activeTab === tab?.id
                    ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-card-foreground hover:border-border'
                }`}
              >
                <Icon name={tab?.icon} size={16} />
                <span>{tab?.label}</span>
              </button>
            ))}
          </nav>
        </div>
      </div>
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Notifications Panel */}
        {unreadNotifications > 0 && (
          <div className="mb-6 bg-primary/5 border border-primary/20 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-primary">Recent Updates</h3>
              <Button variant="ghost" size="sm" iconName="X" />
            </div>
            <div className="space-y-2">
              {notifications?.filter(n => !n?.read)?.slice(0, 3)?.map((notification) => (
                <div key={notification?.id} className="flex items-center space-x-3 p-2 bg-background rounded-lg">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${getNotificationColor(notification?.severity)}`}>
                    <Icon name={getNotificationIcon(notification?.type)} size={14} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-card-foreground">{notification?.message}</p>
                    <p className="text-xs text-muted-foreground">{formatDate(notification?.timestamp)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tab Content */}
        <div className="space-y-8">
          {activeTab === 'overview' && (
            <>
              <ITMOMetadataPanel 
                selectedCredits={selectedCredits}
                onBulkAction={handleBulkAction}
              />
              <AuthorizationTable 
                onSelectionChange={handleSelectionChange}
                onCreditAction={handleCreditAction}
              />
            </>
          )}

          {activeTab === 'authorizations' && (
            <AuthorizationTable 
              onSelectionChange={handleSelectionChange}
              onCreditAction={handleCreditAction}
            />
          )}

          {activeTab === 'conflicts' && (
            <DoubleCountingPreventionPanel 
              onResolveConflict={handleResolveConflict}
            />
          )}

          {activeTab === 'reports' && (
            <ComplianceReportGenerator 
              onGenerateReport={handleGenerateReport}
            />
          )}
        </div>
      </div>
      {/* Mobile Navigation Helper */}
      <div className="lg:hidden fixed bottom-4 right-4">
        <Button variant="default" size="sm" iconName="Menu">
          Quick Actions
        </Button>
      </div>
    </div>
  );
};

export default Article6Gateway;